/* eslint-disable @typescript-eslint/naming-convention */
import React, { memo, useMemo } from "react";

import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { Loader } from "@swiggy-private/dls-web-core/dist/components/Loader";
import { TURBO_PAYMENT_METHOD_WIDGET_DATA_TEST_IDS } from "@swiggy-private/payment-test-ids";
import { isCompactScreenWidth, useFlowThemeOverride, useScreenWidthSize } from "@swiggy-private/checkout-core";
import { ThemeProvider } from "@swiggy-private/dls-web-theme/dist/components/ThemeProvider";
import { format } from "@swiggy-private/js-utils";

import { PAY_TEXT, PAY_USING_ACCESSIBILITY_LABEL, SLIDE_TO_PAY_AMOUNT } from "@payment-ui/constants/TurboPayment";
import {
    resolveTurboPayButtonThemeFlow,
    TURBO_PAY_BUTTON_THEME_OVERRIDES,
} from "@payment-ui/constants/TurboPayButtonTheme";

import { DiscountAmount } from "./styles";
import {
    LoaderWrapper,
    QuickCheckoutPayButton as StyledQuickCheckoutPayButton,
    Subtitle,
    Title,
} from "./quickCheckoutStyles";
import QuickCheckoutItemCountLabel from "./QuickCheckoutItemCountLabel";

interface QuickCheckoutTurboV2PaymentButtonProps {
    finalAmount: string;
    discountAmount?: string;
    onPayClick: VoidFunction;
    dataTestId?: string;
    paymentMethodName?: string;
    shouldShowLoadingState?: boolean;
    totalItemCount?: number;
}

const FINAL_PRICE_TEXT = "Final Price";

function QuickCheckoutTurboV2PaymentButton({
    finalAmount,
    onPayClick,
    discountAmount,
    dataTestId,
    paymentMethodName,
    shouldShowLoadingState = false,
    totalItemCount,
}: QuickCheckoutTurboV2PaymentButtonProps) {
    const accessibilityLabel = paymentMethodName
        ? format(PAY_USING_ACCESSIBILITY_LABEL, format(SLIDE_TO_PAY_AMOUNT, finalAmount), paymentMethodName)
        : `${PAY_TEXT} ${format(SLIDE_TO_PAY_AMOUNT, finalAmount)}`;

    const flow = resolveTurboPayButtonThemeFlow({ isQuickCheckoutEnabled: true, isPop: false });
    const { customTheme, cssVariables, baseTheme } = useFlowThemeOverride({
        flow,
        overridesByFlow: TURBO_PAY_BUTTON_THEME_OVERRIDES,
    });
    const screenWidthSize = useScreenWidthSize();
    const shouldShowItemCount = useMemo(
        () => !!totalItemCount && !isCompactScreenWidth(screenWidthSize),
        [totalItemCount, screenWidthSize],
    );

    const payButtonText = (
        <Text
            data-testid={TURBO_PAYMENT_METHOD_WIDGET_DATA_TEST_IDS.PAY_BUTTON_TEXT}
            variant="V2.body1.neutral.bold"
            color="text_Button_Primary"
            as="span"
        >
            {PAY_TEXT}{" "}
            {discountAmount && discountAmount !== finalAmount && (
                <Text
                    data-testid={TURBO_PAYMENT_METHOD_WIDGET_DATA_TEST_IDS.PAY_BUTTON_DISCOUNT}
                    variant="V2.overline.neutral.medium"
                    color="text_inverse_Med_Emphasis"
                    as="span"
                    aria-hidden="true"
                >
                    <DiscountAmount>{format(SLIDE_TO_PAY_AMOUNT, discountAmount)}</DiscountAmount>
                </Text>
            )}
            {format(SLIDE_TO_PAY_AMOUNT, finalAmount)}
        </Text>
    );

    return (
        <ThemeProvider theme={customTheme} colorMode={baseTheme.colorMode} design_language={baseTheme.designLanguage}>
            <div style={cssVariables}>
                <StyledQuickCheckoutPayButton
                    variant="positive"
                    onClick={shouldShowLoadingState ? undefined : onPayClick}
                    data-testid={dataTestId}
                    isLoading={shouldShowLoadingState}
                    aria-label={accessibilityLabel}
                >
                    {shouldShowLoadingState ? (
                        <LoaderWrapper data-testid={TURBO_PAYMENT_METHOD_WIDGET_DATA_TEST_IDS.PAY_BUTTON_LOADER}>
                            <Loader
                                size="medium"
                                type="primary"
                                foregroundColor="text_Button_Primary"
                                backgroundColor="text_inverse_disabled"
                            />
                        </LoaderWrapper>
                    ) : (
                        <>
                            <Title>
                                {shouldShowItemCount && (
                                    <QuickCheckoutItemCountLabel totalItemCount={totalItemCount as number} />
                                )}
                                {payButtonText}
                            </Title>
                            <Subtitle>
                                <Text variant="V2.overline.neutral.bold" color="special_Price_AIP_Final_Price">
                                    {FINAL_PRICE_TEXT}
                                </Text>
                            </Subtitle>
                        </>
                    )}
                </StyledQuickCheckoutPayButton>
            </div>
        </ThemeProvider>
    );
}

export default memo(QuickCheckoutTurboV2PaymentButton);
